/**
 * Sync-Manager – koppelt das Gerät mit einem Sync-Bereich und hält Sitzungen,
 * Einstellungen und Zeichenzähler mit den anderen Geräten abgeglichen.
 *
 * Ein Abgleich: Stand vom Server holen, mit dem eigenen zusammenführen, das
 * Ergebnis hier übernehmen und – falls der Server etwas Neues bekommt –
 * hochladen. Hat ein anderes Gerät zwischendurch geschrieben (412), beginnt
 * das Ganze von vorn. Danach gehen fehlende Aufnahmen hoch.
 *
 * Angestoßen wird beim Start, kurz nach jeder eigenen Änderung, wenn der Tab
 * wieder sichtbar wird und wenn das Netz zurückkommt.
 */

import * as sessionManager from '../sessionManager.js';
import * as settingsManager from '../settingsManager.js';
import * as usageTracker from '../usageTracker.js';
import { createClient, SyncConflictError } from './syncClient.js';
import {
  loadConfig,
  saveConfig,
  clearConfig,
  normalizeServerUrl,
  encodePairingCode,
  decodePairingCode,
} from './syncConfig.js';
import { connect, disconnect, getConnection } from './syncConnection.js';
import {
  generateSecret,
  encrypt,
  decrypt,
  packJson,
  unpackJson,
  toBase64Url,
} from './syncCrypto.js';
import { mergeState, sameState, isSame, STATE_FORMAT, SYNCED_SETTINGS } from './syncState.js';
import { sanitizeRemoteState } from './syncValidate.js';
import { uploadMissing } from './syncAudio.js';

const STATUS_KEY = 'the-reader-sync-status';
const MAX_ATTEMPTS = 3;
/** Wartezeit nach einer Änderung, damit schnelle Folgeänderungen gebündelt gehen */
const DEBOUNCE_MS = 2000;

/**
 * @typedef {Object} SyncStatus
 * @property {boolean} paired
 * @property {boolean} syncing
 * @property {number|null} lastSyncAt
 * @property {string|null} error
 */

/** @type {SyncStatus} */
let status = { paired: false, syncing: false, lastSyncAt: null, error: null };

let running = null;
let runAgain = false;
let debounceTimer = null;
/** Während abgeglichene Daten übernommen werden, lösen sie keinen neuen Abgleich aus */
let applying = false;

const statusListeners = [];
const syncedListeners = [];

/**
 * Stellt die gespeicherte Kopplung wieder her und meldet sich für Änderungen an
 */
export async function init() {
  status = { ...status, ...readStoredStatus() };
  sessionManager.onChange(scheduleSync);
  usageTracker.onUpdate(() => !applying && scheduleSync());
  settingsManager.onChange((keys) => {
    if (keys.some((key) => SYNCED_SETTINGS.includes(key))) scheduleSync();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') syncNow();
  });
  window.addEventListener('online', () => syncNow());

  const config = loadConfig();
  if (!config) return;
  await connect(config);
  setStatus({ paired: true });
  syncNow();
}

/**
 * Prüft Server-Adresse und Zugangsschlüssel
 * @param {string} serverUrlInput
 * @param {string} token
 * @returns {Promise<{ serverUrl: string, token: string }>}
 */
export async function checkServer(serverUrlInput, token) {
  const serverUrl = normalizeServerUrl(serverUrlInput);
  if (!token.trim()) throw new Error('Bitte den Zugangsschlüssel des Servers eingeben.');
  await createClient({ serverUrl, token: token.trim() }).ping();
  return { serverUrl, token: token.trim() };
}

/**
 * Legt einen neuen Sync-Bereich an – für das erste Gerät
 * @param {string} serverUrlInput
 * @param {string} token
 * @returns {Promise<string>} Kopplungscode für die weiteren Geräte
 */
export async function createSpace(serverUrlInput, token) {
  const server = await checkServer(serverUrlInput, token);
  const config = { ...server, secret: toBase64Url(generateSecret()) };
  await pair(config);
  return encodePairingCode(config);
}

/**
 * Koppelt dieses Gerät mit einem bestehenden Sync-Bereich
 * @param {string} code
 */
export async function pairWithCode(code) {
  const config = decodePairingCode(code);
  await createClient(config).ping();
  await pair(config);
}

/**
 * Speichert die Kopplung und gleicht zum ersten Mal ab. Scheitert das, wird die
 * Kopplung zurückgenommen – ein halb gekoppeltes Gerät hilft niemandem.
 */
async function pair(config) {
  saveConfig(config);
  await connect(config);
  setStatus({ paired: true, lastSyncAt: null, error: null });
  await syncNow();
  const { error } = getStatus();
  if (error) {
    unpair();
    throw new Error(error);
  }
}

/**
 * Trennt das Gerät vom Sync-Bereich. Die Daten bleiben hier und auf dem Server.
 */
export function unpair() {
  clearTimeout(debounceTimer);
  clearConfig();
  disconnect();
  localStorage.removeItem(STATUS_KEY);
  setStatus({ paired: false, syncing: false, lastSyncAt: null, error: null });
}

/**
 * @returns {string|null} der Kopplungscode dieses Geräts
 */
export function currentPairingCode() {
  const config = loadConfig();
  return config ? encodePairingCode(config) : null;
}

/**
 * Gleicht sofort ab. Läuft schon ein Abgleich, folgt direkt danach ein weiterer.
 * @returns {Promise<void>}
 */
export function syncNow() {
  if (!getConnection()) return Promise.resolve();
  if (running) {
    runAgain = true;
    return running;
  }
  running = runSyncSafely().finally(() => {
    running = null;
    if (runAgain) {
      runAgain = false;
      scheduleSync();
    }
  });
  return running;
}

function scheduleSync() {
  if (!getConnection()) return;
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(syncNow, DEBOUNCE_MS);
}

async function runSyncSafely() {
  const connection = getConnection();
  setStatus({ syncing: true });
  try {
    const changes = await syncState(connection);
    if (getConnection() !== connection) return; // inzwischen getrennt
    setStatus({ syncing: false, lastSyncAt: Date.now(), error: null });
    for (const listener of syncedListeners) listener(changes);
    await uploadMissing();
  } catch (error) {
    if (getConnection() !== connection) return;
    console.warn('Abgleich gescheitert:', error);
    setStatus({ syncing: false, error: error.message });
  }
}

/** Abgleich, dessen Gerät inzwischen getrennt wurde – endet still */
class StaleSyncError extends Error {}

/**
 * @param {import('./syncConnection.js').Connection} connection
 */
function ensureStillConnected(connection) {
  if (getConnection() !== connection) throw new StaleSyncError();
}

/**
 * Gleicht den Zustand ab und liefert, was sich auf diesem Gerät geändert hat
 * @param {import('./syncConnection.js').Connection} connection
 * @returns {Promise<{ sessionIds: string[], settings: boolean, usage: boolean }>}
 */
async function syncState(connection) {
  const { client, keys } = connection;
  for (let attempt = 1; ; attempt++) {
    const remote = await fetchRemoteState(connection);
    const local = await collectLocalState();
    const merged = mergeState(local, remote?.state ?? null);
    ensureStillConnected(connection);
    const changes = await applyToDevice(local, merged);
    if (sameState(merged, remote?.state ?? null)) return changes;

    try {
      const data = await encrypt(keys.contentKey, await packJson(merged));
      ensureStillConnected(connection);
      await client.putState(keys.spaceId, data, remote?.etag ?? null);
      return changes;
    } catch (error) {
      if (!(error instanceof SyncConflictError) || attempt >= MAX_ATTEMPTS) throw error;
    }
  }
}

/**
 * @param {import('./syncConnection.js').Connection} connection
 * @returns {Promise<{ state: import('./syncState.js').SyncState, etag: string }|null>}
 */
async function fetchRemoteState({ client, keys }) {
  const remote = await client.getState(keys.spaceId);
  if (!remote) return null;

  let state;
  try {
    state = await unpackJson(await decrypt(keys.contentKey, remote.data));
  } catch {
    throw new Error('Die Daten auf dem Server passen nicht zum Kopplungscode.');
  }
  if (state?.format > STATE_FORMAT) {
    throw new Error(
      'Ein anderes Gerät nutzt eine neuere Version des Readers. Bitte aktualisieren.'
    );
  }
  return { state: sanitizeRemoteState(state), etag: remote.etag };
}

/** @returns {Promise<import('./syncState.js').SyncState>} */
async function collectLocalState() {
  return {
    format: STATE_FORMAT,
    sessions: await sessionManager.getAllRecords(),
    settings: settingsManager.getSyncedSettings(SYNCED_SETTINGS),
    usage: usageTracker.getCounters(),
  };
}

/**
 * Übernimmt den zusammengeführten Stand auf diesem Gerät, soweit er abweicht
 * @param {import('./syncState.js').SyncState} local
 * @param {import('./syncState.js').SyncState} merged
 */
async function applyToDevice(local, merged) {
  const localById = new Map(local.sessions.map((session) => [session.id, session]));
  const changedSessions = merged.sessions.filter(
    (session) => !isSame(localById.get(session.id), session)
  );
  const settingsChanged = !isSame(local.settings, merged.settings);
  const usageChanged = !isSame(local.usage, merged.usage);

  applying = true;
  try {
    if (changedSessions.length > 0) await sessionManager.applyRecords(changedSessions);
    if (settingsChanged) settingsManager.applySyncedSettings(merged.settings);
    if (usageChanged) usageTracker.applyCounters(merged.usage);
  } finally {
    applying = false;
  }
  return {
    sessionIds: changedSessions.map((session) => session.id),
    settings: settingsChanged,
    usage: usageChanged,
  };
}

/** @returns {SyncStatus} */
export function getStatus() {
  return { ...status };
}

/**
 * @param {(status: SyncStatus) => void} callback
 */
export function onStatus(callback) {
  statusListeners.push(callback);
}

/**
 * Meldet nach jedem erfolgreichen Abgleich, was sich hier geändert hat
 * @param {(changes: { sessionIds: string[], settings: boolean, usage: boolean }) => void} callback
 */
export function onSynced(callback) {
  syncedListeners.push(callback);
}

function setStatus(changes) {
  status = { ...status, ...changes };
  try {
    localStorage.setItem(
      STATUS_KEY,
      JSON.stringify({ lastSyncAt: status.lastSyncAt, error: status.error })
    );
  } catch {
    // Nur Anzeige – ohne Speicher fehlt nach dem Neuladen der letzte Zeitpunkt
  }
  for (const listener of statusListeners) listener(getStatus());
}

function readStoredStatus() {
  try {
    return JSON.parse(localStorage.getItem(STATUS_KEY)) ?? {};
  } catch {
    return {};
  }
}
